
import {defineComponent} from "vue";
import {useToast} from "vue-toastification";
import {isEmptyString, truncateString} from "@/helpers/functions/string";
import {ErrorHandlerQueue} from "@/error/ErrorHandlerQueue";
import {DpExceptionsErrorHandler} from "@/error/handlers/DpExceptionsErrorHandler";
import {UIStateDto} from "@/dtos/UIStateDto";
import {AxiosErrorHandler} from "@/error/handlers/AxiosErrorHandler";
import AlertError2 from "@/components/UI/Bootstrap/Alert/AlertError2.vue";
import {PageDetailModeEnum} from "@/models/enum/PageDetailModeEnum";
import BaseOffcanvas from "@/components/UI/Bootstrap/Offcanvas/BaseOffcanvas.vue";
import {
  CustomerMainCategoryOverviewRestDto,
  DpMainCategoryOverviewRestDto,
  MainProductCategoriesOverviewResultsRestDto,
  MasterMainProductCategoriesOverviewResultsRestDto,
  StatusValueRestDto
} from "@/api/pharma-cpc-product-category-mgmt/models";
import {ProductCategoryRestService} from "@/services/rest/cpc-product-category-mgmt/ProductCategoryRestService";
import BaseOffcanvasButtonOpen from "@/components/UI/Bootstrap/Offcanvas/BaseOffcanvasButtonOpen.vue";
import BaseTitle from "@/components/UI/BaseTitle.vue";
import BaseContainer from "@/components/UI/Bootstrap/BaseContainer.vue";
import {PropType} from "vue/dist/vue";
import DpMainCategoryRow from "@/components/layouts/catalog/categories/DpMainCategoryRow.vue";
import CustomerMainCategoryRow from "@/components/layouts/catalog/categories/CustomerMainCategoryRow.vue";
import {ValueEnum} from "@/api/enums/value-enum";
import CustomerMainCategory from "@/components/layouts/catalog/categories/CustomerMainCategory.vue";
import DPMainCategory from "@/components/layouts/catalog/categories/DPMainCategory.vue";

type CategoryItem = {
  type: 'dp' | 'customer';
  category: DpMainCategoryOverviewRestDto | CustomerMainCategoryOverviewRestDto;
  sortWeight: number;
  name: string;
};

export default defineComponent({
  name: "MainCategories",
  components: {
    CustomerMainCategory,
    DPMainCategory,
    CustomerMainCategoryRow,
    DpMainCategoryRow,
    BaseContainer,
    BaseTitle,
    BaseOffcanvasButtonOpen,
    BaseOffcanvas,
    AlertError2
  },
  props: {
    language: {
      type: String,
      required: true
    },
    allLanguages: {
      type: Array as PropType<string[]>,
      required: true
    },
  },
  data() {
    return {
      toaster: useToast(),
      pageUI: UIStateDto.createWithDefaults(),
      categories: null as MainProductCategoriesOverviewResultsRestDto | null,
      masterCategories: null as MasterMainProductCategoriesOverviewResultsRestDto | null,
      customerMainCategoryMode: PageDetailModeEnum.NEW as PageDetailModeEnum,
      customerMainCategoryCode: null as string | null,
      dpMainCategory: null as DpMainCategoryOverviewRestDto | null,
      customerMainCategorySequence: 0 as number,
      dpMainCategorySequence: 0 as number,
    }
  },
  mounted() {
    this.reloadContent();
  },
  watch: {
    language: {
      handler() {
        this.reloadContent();
      },
      immediate: false
    }
  },
  computed: {
    PageDetailModeEnum() {
      return PageDetailModeEnum
    },
    sortedCategories(): CategoryItem[] {
      if (!this.categories) return [];

      const categoryItems: CategoryItem[] = [];

      // Add DP main categories
      this.categories.dp_main_categories.forEach(dpCategory => {
        const sortWeight = dpCategory.modified_sort_weight ?? dpCategory.sort_weight ?? 10000;
        const name = dpCategory.modified_name ?? dpCategory.category.description;

        categoryItems.push({
          type: 'dp',
          category: dpCategory,
          sortWeight,
          name
        });
      });

      // Add Customer main categories
      this.categories.customer_main_categories.forEach(customerCategory => {
        const sortWeight = customerCategory.sort_weight ?? 10000;
        const name = customerCategory.category.description;

        categoryItems.push({
          type: 'customer',
          category: customerCategory,
          sortWeight,
          name
        });
      });

      // Sort by sortWeight first, then alphabetically by name
      return categoryItems.sort((a, b) => {
        if (a.sortWeight !== b.sortWeight) {
          return a.sortWeight - b.sortWeight;
        }
        return a.name.localeCompare(b.name);
      });
    },
    rootCategoryCode(): string {
      if (!this.masterCategories?.dp_main_categories.length) {
        return '';
      }

      return this.masterCategories.dp_main_categories[0].path_by_code.split('|')[0];
    }
  },
  methods: {
    truncateString,
    isEmptyString,
    getCategoryKey(categoryItem: CategoryItem): string {
      return `${categoryItem.type}-${categoryItem.category.category.code}`;
    },
    async reloadContent() {
      this.pageUI
        .clearError()
        .setNotReady();
      try {
        this.categories = await ProductCategoryRestService.getInstance().findCatalogMainProductCategories(this.language);
        this.masterCategories = await ProductCategoryRestService.getInstance().findCatalogMasterMainProductCategories();
      } catch (exceptions: any) {
        this.setExceptions(exceptions);
        window.scrollTo(0, 0);
      } finally {
        this.pageUI.setReady();
      }
    },
    async afterSave(message: string | null = null) {
      this.toaster.success(message ?? this.$t('savedSuccessfully'));
      await this.reloadContent();
    },
    setExceptions(exceptions: unknown): void {
      ErrorHandlerQueue
        .create()
        .add(DpExceptionsErrorHandler.createWithDefaultUIStateBehavior(this.pageUI as UIStateDto))
        .add(AxiosErrorHandler.createWithDefaultUIStateBehavior(this.pageUI as UIStateDto))
        .catch(exceptions, true);
    },
    prepareCustomerOffCanvasForNewCategory(): void {
      this.customerMainCategoryCode = null;
      this.customerMainCategoryMode = PageDetailModeEnum.NEW;
      this.customerMainCategorySequence++;
    },
    prepareCustomerOffCanvasForEditCategory(category: CustomerMainCategoryOverviewRestDto): void {
      this.customerMainCategoryCode = category.category.code;
      this.customerMainCategoryMode = PageDetailModeEnum.EDIT;
      this.customerMainCategorySequence++;
    },
    prepareDPOffCanvasForEditCategory(category: DpMainCategoryOverviewRestDto): void {
      this.dpMainCategory = category;
      this.dpMainCategorySequence++;
    },
    getMasterCategory(code: string): DpMainCategoryOverviewRestDto {
      return this.masterCategories!.dp_main_categories.find(category => category.category.code === code)!;
    },
    async updateStatus(code: string, status: ValueEnum): Promise<void> {
      this.pageUI
        .setNotReady()
        .clearError();

      try {
        const statusValue: StatusValueRestDto = { value: status };
        await ProductCategoryRestService.getInstance().updateStatus(statusValue, code);
        await this.afterSave();
      } catch (exceptions: unknown) {
        this.setExceptions(exceptions);
      } finally {
        this.pageUI.setReady();
      }
    },
    async deleteCustomerMainCategory(category: CustomerMainCategoryOverviewRestDto): Promise<void> {
      const answer = window.confirm(this.$t('deleteConfirmation'));

      if (answer) {
        this.pageUI
          .setNotReady()
          .clearError();

        try {
          await ProductCategoryRestService.getInstance().deleteCustomerProductCategory(category.category.code);
          await this.afterSave(this.$t('deletedSuccessfully'));
        } catch (exceptions: any) {
          this.setExceptions(exceptions);
        } finally {
          this.pageUI.setReady();
        }
      }
    },
  },
});
